import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import { ReadLocationFeatureSubscriptionsResponse } from '../models/location-feature-subscription-response';
import { ReadTenantFeatureSubscriptionsResponse } from '../models/read-tenant-feature-subscriptions-response';
import { UpdateLocationFeatureSubscriptionsCommand } from '../models/update-location-feature-subscriptions-command';
import { UpdateTenantFeatureSubscriptionsCommand } from '../models/update-tenant-feature-subscriptions-command';

const featureSubscriptionsBaseUrl = '/api/v1/featuresubscriptions';

enum FeatureSubscriptionsQueryPaths {
  ReadTenantFeatureSubscriptions = 'ReadTenantFeatureSubscriptions',
  ReadLocationFeatureSubscriptions = 'ReadLocationFeatureSubscriptions',
  UpdateTenantFeatureSubscriptions = 'UpdateTenantFeatureSubscriptions',
  UpdateLocationFeatureSubscriptions = 'UpdateLocationFeatureSubscriptions',
}

const readTenantFeatureSubscriptions = async (axiosInstance: AxiosInstance): Promise<ReadTenantFeatureSubscriptionsResponse> => {
  const response = await axiosInstance.get(
    `${featureSubscriptionsBaseUrl}/${FeatureSubscriptionsQueryPaths.ReadTenantFeatureSubscriptions}`,
  );
  return response.data;
};

const readLocationFeatureSubscriptions = async (axiosInstance: AxiosInstance): Promise<ReadLocationFeatureSubscriptionsResponse> => {
  const response = await axiosInstance.get(
    `${featureSubscriptionsBaseUrl}/${FeatureSubscriptionsQueryPaths.ReadLocationFeatureSubscriptions}`,
  );
  return response.data;
};

const updateTenantFeatureSubscriptions = async (
  axiosInstance: AxiosInstance,
  command: UpdateTenantFeatureSubscriptionsCommand,
): Promise<void> => {
  await axiosInstance.put(`${featureSubscriptionsBaseUrl}/${FeatureSubscriptionsQueryPaths.UpdateTenantFeatureSubscriptions}`, command);
};

const updateLocationFeatureSubscriptions = async (
  axiosInstance: AxiosInstance,
  command: UpdateLocationFeatureSubscriptionsCommand,
): Promise<void> => {
  await axiosInstance.put(`${featureSubscriptionsBaseUrl}/${FeatureSubscriptionsQueryPaths.UpdateLocationFeatureSubscriptions}`, command);
};

export const useReadTenantFeatureSubscriptions = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [FeatureSubscriptionsQueryPaths.ReadTenantFeatureSubscriptions],
    queryFn: () => readTenantFeatureSubscriptions(authenticatedInstance),
  });
};

export const useReadLocationFeatureSubscriptions = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [FeatureSubscriptionsQueryPaths.ReadLocationFeatureSubscriptions],
    queryFn: () => readLocationFeatureSubscriptions(authenticatedInstance),
  });
};

export const useUpdateTenantFeatureSubscriptions = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: UpdateTenantFeatureSubscriptionsCommand) => updateTenantFeatureSubscriptions(authenticatedInstance, command),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FeatureSubscriptionsQueryPaths.ReadTenantFeatureSubscriptions] });
    },
  });
};

export const useUpdateLocationFeatureSubscriptions = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (command: UpdateLocationFeatureSubscriptionsCommand) => updateLocationFeatureSubscriptions(authenticatedInstance, command),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FeatureSubscriptionsQueryPaths.ReadLocationFeatureSubscriptions] });
    },
  });
};
